import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Zap, KeyRound, Loader2, ArrowLeft, Mail } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../services/supabaseClient';
import { usePageMeta } from '../hooks/usePageMeta';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Speed Reading App Login | Flash-Read',
    description: 'Sign in to your Flash-Read account to access your reading progress, achievements, streaks, and personalized speed reading dashboard.',
  });

  const [view, setView] = useState<'signin' | 'reset'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    if (!isSupabaseConfigured) {
      setMessage({ type: 'error', text: 'Supabase credentials missing. Check .env file.' });
      setLoading(false);
      return;
    }

    try {
      if (view === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        // Navigation is handled by App.tsx via onAuthStateChange
      } else if (view === 'reset') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setMessage({ type: 'success', text: 'Password reset link sent to your email.' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'An error occurred' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 flex flex-col items-center justify-center px-4 bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-8">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <div className="bg-blue-100 dark:bg-blue-900/30 p-4 rounded-full mb-4">
             <Zap className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            {view === 'signin' && 'Welcome Back'}
            {view === 'reset' && 'Reset Password'}
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-2 text-center text-sm">
            {view === 'signin' && 'Sign in to access your profile and progress.'}
            {view === 'reset' && 'Enter your email to receive a reset link.'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleAuth} className="space-y-4">
          
          {/* Email Input */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
              <input 
                type="email" 
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary outline-none transition-all text-slate-900 dark:text-white placeholder-slate-400"
                placeholder="you@example.com"
              />
            </div>
          </div>

          {/* Password Input (Hidden for Reset) */}
          {view !== 'reset' && (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Password</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                <input 
                  type="password" 
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary outline-none transition-all text-slate-900 dark:text-white placeholder-slate-400"
                  placeholder="••••••••"
                />
              </div>
            </div>
          )}

          {/* Error/Success Messages */}
          {message && (
            <div className={`text-sm p-3 rounded-lg flex items-center ${
              message.type === 'error' 
                ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' 
                : 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400'
            }`}>
              {message.text}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-primary hover:bg-blue-600 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/20 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                {view === 'signin' && <LogIn className="w-5 h-5" />}
                {view === 'reset' && <Mail className="w-5 h-5" />}
                <span>
                  {view === 'signin' && 'Sign In'}
                  {view === 'reset' && 'Send Reset Link'}
                </span>
              </>
            )}
          </button>
        </form>

        {/* Footer / Toggle Modes */}
        <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800 flex flex-col space-y-3 text-sm text-center">
          
          {view === 'signin' && (
            <>
              <button 
                onClick={() => setView('reset')}
                className="text-slate-500 hover:text-primary transition-colors"
              >
                Forgot your password?
              </button>
              <div className="text-slate-400">
                Don't have an account?{' '}
                <button 
                  onClick={() => navigate('/signup')}
                  className="font-semibold text-primary hover:underline"
                >
                  Sign Up
                </button>
              </div>
            </>
          )}

          {view === 'reset' && (
            <button 
              onClick={() => setView('signin')}
              className="flex items-center justify-center space-x-1 text-slate-500 hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Sign In</span>
            </button>
          )}

        </div>
      </div>
    </div>
  );
};

export default LoginPage;